import {useCallback, useMemo} from 'react';
import Template from '../../../models/Template';
import {Scene} from '../../../scene/types';
import {
    hiddenInputKeys,
    inputRows,
    isToggleOn,
    Toggle,
    togglesOf,
} from '../../../scene/rowVisibility';
import {useMergedScene, useTemplate} from '../../../context/TemplateContext';

/** Every design's scene: a row key is one row across designs. */
export const designScenes = (template: Template | undefined): Scene[] =>
    (template?.designs ?? []).flatMap((design) => (design.scene ? [design.scene] : []));

export interface Rows {
    /** Input key → the row it belongs to. */
    rowOf: ReadonlyMap<string, string>;
    /** This design's row keys, in the importer's order. */
    keys: string[];
    hidden: ReadonlySet<string>;
    /** Fields switched off with their layer. A hidden row's fields are not
     *  here: they stay listed with the row. */
    off: ReadonlySet<string>;
    /** This design's switches; template-wide ones have no `row`. */
    toggles: Toggle[];
    isOn: (toggle: Toggle) => boolean;
    setToggle: (toggle: Toggle, on: boolean) => void;
    hide: (key: string) => void;
    show: (key: string) => void;
}

/** Rows and switches for the design on screen, bound to the editor's state
 *  (docs/row-groups.md §6). */
export const useRows = (designIndex: number): Rows => {
    const {template, visibility, setVisibility} = useTemplate();
    const design = template?.designs?.[designIndex];
    const visible = useMergedScene(design, designIndex);
    const scenes = useMemo(() => designScenes(template), [template]);
    const defs = template?.toggles;

    const rowOf = useMemo(() => inputRows(scenes), [scenes]);
    // Only this design's: another design's row or switch would do nothing here.
    const keys = useMemo(() => (design?.scene?.rows ?? []).map((row) => row.key), [design?.scene]);
    const toggles = useMemo(() => (design?.scene ? togglesOf(design.scene, defs) : []), [design?.scene, defs]);

    const hidden = useMemo(() => new Set(visibility.hiddenRows), [visibility.hiddenRows]);
    const off = useMemo(() => {
        if (!design?.scene || !visible) return new Set<string>();
        const gone = [...hiddenInputKeys(design.scene, visible)];
        return new Set(gone.filter((key) => !hidden.has(rowOf.get(key) ?? '')));
    }, [design?.scene, visible, hidden, rowOf]);

    const isOn = useCallback((toggle: Toggle) => isToggleOn(toggle, visibility), [visibility]);
    // Back at its default = no override (docs/row-groups.md §5).
    const setToggle = useCallback((toggle: Toggle, on: boolean) => setVisibility((prev) => {
        const {[toggle.key]: _, ...rest} = prev.toggles;
        return {...prev, toggles: on === toggle.defaultOn ? rest : {...rest, [toggle.key]: on}};
    }), [setVisibility]);
    const hide = useCallback((key: string) => setVisibility((prev) => (
        prev.hiddenRows.includes(key) ? prev : {...prev, hiddenRows: [...prev.hiddenRows, key]}
    )), [setVisibility]);
    const show = useCallback((key: string) => setVisibility((prev) => (
        {...prev, hiddenRows: prev.hiddenRows.filter((row) => row !== key)}
    )), [setVisibility]);

    return {rowOf, keys, hidden, off, toggles, isOn, setToggle, hide, show};
};
